/**
 * The model builders: bytes on one side, a `BuiltBimModel` on the other.
 *
 * Carved out of `composables/useBimScene.ts` ? a single 3,600-line function ?
 * with the code moved verbatim. What moved is the whole "build" half of the
 * import pipeline, which was already a closed contract in that file: the
 * `BuiltBimModel` interface documents it as the seam between a builder and
 * `installModelGroup`, and none of these functions touched the scene's state.
 *
 *   ? `collectObjectLayer` ? the GLB/glTF object layer (card t_cb0aa19b);
 *   ? `modelMeshCount` ? the rig hook's own count;
 *   ? `buildGltfModel` ? a GLB/glTF through three's own GLTFLoader;
 *   ? `buildUsdModel` ? a USDZ through three's own USDLoader (card t_92f7411f).
 *
 * The IFC builder stayed behind: it is the one path that streams into the live
 * scene's shared material and texture caches, and it is inline in
 * `loadModelFromUrl` rather than a builder of its own.
 *
 * The ONE thing these builders cannot own is the visibility rule: a mesh's own
 * first-frame visibility is decided by `applyMeshVisibility`, which reads the
 * scene's live hidden-layer and hidden-object sets. It arrives as a parameter
 * rather than being re-created, so there is still exactly one rule.
 */
import * as THREE from 'three';
import type { BimModelFormat } from './bimModelFormat';
import { elementTypeNameFromObjectName } from './bimNav';
import {
  bimObjectKeyOf,
  bimObjectRows,
  type BimObjectInfo,
  type BimObjectNodeFacts,
} from './bimObjects';
// Type-only: the element row a built model reports. Erased at runtime, so the
// composable may import this module back without a cycle in the emitted code.
import type { IfcElementInfo } from '../composables/useBimScene';

/**
 * One model's worth of scene content, whatever format it was built from (card
 * t_14696d19) — the contract between a builder (`buildIfcModel`,
 * `buildGltfModel`) and `installModelGroup`, which puts it on screen.
 */
export interface BuiltBimModel {
  /** Which loader built this model — the split's own fact, logged and probed. */
  format: BimModelFormat;
  /** The group that enters the scene (marked `userData.__bimGroup`); its meshes hang inside it. */
  group: THREE.Group;
  /** Every mesh of the model — picking, the occlusion cull, the shadow frustum. */
  meshes: THREE.Mesh[];
  /** Element → meshes. IFC only: a glTF has no semantic tree (empty map). */
  elementMeshes: Map<number, THREE.Mesh[]>;
  /** The element list the panel and the editor read. Empty for a glTF. */
  elements: IfcElementInfo[];
  /**
   * The OBJECT list a GLB/glTF model carries instead of an element tree (card
   * t_cb0aa19b): one row per named node, keyed by the node's own IFC id when its
   * extras carried one and by its name otherwise (`utils/bimObjects.ts`). An IFC
   * build leaves it empty — its tree is the element list.
   */
  objects: BimObjectInfo[];
  /**
   * Object key → the meshes that object owns (its own and its subtree's). What a
   * visibility toggle re-evaluates, what the selection highlight frames, and
   * what a device anchor resolves its local point against. Empty for an IFC.
   */
  objectMeshes: Map<string, THREE.Mesh[]>;
  /** web-ifc's coordination matrix; `null` for a glTF (there is no IFC frame). */
  coordinationMatrix: number[] | null;
}

/**
 * Build the object layer of a freshly loaded glTF/USD group. `root` is the
 * group about to enter the scene; the walk starts at its CHILDREN (the
 * loader's own scene wrapper is not an object of the file — its name is
 * three's `AuxScene`/the file's scene name and means nothing to a user).
 */
export function collectObjectLayer(root: THREE.Object3D): {
  objects: BimObjectInfo[];
  objectMeshes: Map<string, THREE.Mesh[]>;
  unnamedMeshes: number;
} {
  const facts: BimObjectNodeFacts[] = [];
  const paths = new Map<THREE.Mesh, string[]>();

  const visit = (node: THREE.Object3D, depth: number, parentName: string | null, inherited: string[]): number => {
    const isMesh = (node as THREE.Mesh).isMesh === true;
    const ownKey = isMesh
      ? bimObjectKeyOf(node.name, (node.userData ?? {}).expressID)
      : // A group has no mesh of its own: its identity is its name (a group
        // is never keyed by an id — an exporter writes extras per mesh).
        bimObjectKeyOf(node.name, null);
    const chain = ownKey === null ? inherited : [ownKey, ...inherited];
    let subtreeMeshes = 0;
    for (const child of node.children) subtreeMeshes += visit(child, depth + 1, node.name?.trim() || parentName, chain);
    if (isMesh) {
      const mesh = node as THREE.Mesh;
      if (chain.length > 0) mesh.userData.__bimObjectKey = chain[0];
      else delete mesh.userData.__bimObjectKey;
      mesh.userData.__bimObjectPath = chain;
      if (chain.length > 0) paths.set(mesh, chain);
    }
    // A MESH is an object in its own right (card t_cb0aa19b, measured on the
    // served demo GLB: 637 of its 638 nodes are meshes, and a walk that only
    // listed groups listed three rows for a 476-element model). Every node the
    // file names earns a row — the mesh ones are exactly the objects a click
    // lands on — and its count is its own mesh plus its subtree's.
    const ownMeshes = isMesh ? 1 : 0;
    if (ownKey !== null) {
      facts.push({
        name: node.name,
        // The file's own spelling, when the loader kept it: three's GLTFLoader
        // rewrites a node name (`Fasadni zid (pritličje)` → `Fasadni_zid_…`),
        // and the panel should read what the file says.
        label: typeof node.userData?.name === 'string' ? node.userData.name : null,
        expressId: (node.userData ?? {}).expressID as number | null,
        depth,
        parent: parentName,
        meshCount: ownMeshes + subtreeMeshes,
      });
    }
    return ownMeshes + subtreeMeshes;
  };

  for (const child of root.children) visit(child, 0, null, []);

  // The meshes of every key: a mesh belongs to every object on its own path
  // (its own node, and each named ancestor), which is exactly the subtree the
  // row toggles and the box the row frames.
  const objectMeshes = new Map<string, THREE.Mesh[]>();
  for (const [mesh, chain] of paths) {
    for (const key of chain) {
      const bucket = objectMeshes.get(key);
      if (bucket) bucket.push(mesh);
      else objectMeshes.set(key, [mesh]);
    }
  }

  const objects = bimObjectRows(facts);
  return { objects, objectMeshes, unnamedMeshes: modelMeshCount(root) - paths.size };
}

/** How many meshes a subtree carries (the collector's own count, cheap). */
export function modelMeshCount(root: THREE.Object3D): number {
  let count = 0;
  root.traverse((child) => {
    if ((child as THREE.Mesh).isMesh === true) count += 1;
  });
  return count;
}

/**
 * Build a GLB/glTF model's scene content from its bytes — three's own
 * GLTFLoader, no web-ifc (card t_14696d19).
 *
 * glTF is Y-up by spec and every exporter compensates for its own source
 * frame inside the file — the RoomPlan scan the card names carries the Z-up →
 * Y-up rotation on its root node — so the model is displayed exactly as
 * authored, the way any viewer shows it. There is no IFC coordination matrix
 * to undo here: `coordinationMatrix` is null, and `installModelGroup`'s frame
 * step is a no-op for it.
 *
 * A glTF carries no semantic tree — no expressID, no storeys, no element
 * types — so the element map and the element list come back EMPTY. That
 * emptiness is the graceful degradation the card asks for: the element panel
 * reads it as "nothing to select" (the layout hides it), and the OBJECT list
 * (`utils/bimObjects.ts`) is what the file itself names instead.
 */
export async function buildGltfModel(
  bytes: Uint8Array,
  format: BimModelFormat,
  url: string,
  applyMeshVisibility: (mesh: THREE.Mesh) => void,
): Promise<BuiltBimModel> {
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const loader = new GLTFLoader();
  // The base a .gltf's own relative URIs resolve against: the file's own
  // directory. (A GLB is self-contained and carries absolute buffers, so the
  // base is only read by the JSON form.) Files served from the hub's file
  // route are fetched as one buffer, so a .gltf that references external
  // .bin/texture siblings must have them embedded — the GLB and the
  // embedded-buffer .gltf are what this import supports.
  const basePath = url.split(/[?#]/)[0].replace(/[^/]*$/, '');
  // GLTFLoader takes a GLB as one ArrayBuffer and a .gltf as its JSON text.
  // The GLB is copied into an ArrayBuffer of its own — a view can be a window
  // onto a larger buffer, and TS's `ArrayBufferLike` may be shared.
  let payload: string | ArrayBuffer;
  if (format === 'glb') {
    const glbBuffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(glbBuffer).set(bytes);
    payload = glbBuffer;
  } else {
    payload = new TextDecoder().decode(bytes);
  }

  const gltf = await new Promise<THREE.Group>((resolve, reject) => {
    loader.parse(payload, basePath, (loaded) => resolve(loaded.scene), reject);
  });

  const meshes: THREE.Mesh[] = [];
  gltf.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if ((mesh as { isMesh?: boolean }).isMesh !== true) return;
    // The model's own mesh list: picking, the occlusion cull, the shadow
    // frustum and the section cut all read it. It is the ONLY thing a builder
    // owes the scene about its geometry, so it is collected first.
    meshes.push(mesh);
    // The element TYPE, recovered from the node's own name (2026-10-18): a glTF
    // has no element table, but an IFC export keeps its class in every name
    // (`IfcWallStandardCase/Fasadni zid (pritličje).014`), and the walk's walls,
    // floors and stair treads are all read off `typeName` downstream. Writing it
    // here is what makes a GLB walk like the IFC it came from — no walls that
    // block, and no tread to climb, was the owner's report.
    const typeName = elementTypeNameFromObjectName(String(mesh.userData.name ?? mesh.name ?? ''));
    if (typeName !== '') mesh.userData.typeName = typeName;
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    const isTransparent = materials.some((material) => material.transparent === true);
    for (const material of materials) {
      // The IFC path renders DoubleSide for the same reason: the section cut
      // opens a wall, and the inside has to stay painted.
      material.side = THREE.DoubleSide;
    }
    mesh.castShadow = !isTransparent;
    mesh.receiveShadow = true;
  });

  if (meshes.length === 0) throw new Error('The glTF file contained no meshes');

  // The object layer (card t_cb0aa19b), built BEFORE the first paint: the walk
  // writes each mesh's own key and path, and the visibility pass below reads
  // them, so a stored hidden object is honoured on the very first frame.
  const objectLayer = collectObjectLayer(gltf);
  for (const mesh of meshes) applyMeshVisibility(mesh);
  if (objectLayer.unnamedMeshes > 0) {
    console.info(
      `[useBimScene] ${objectLayer.unnamedMeshes} glTF mesh(es) carry neither a name nor an IFC id — drawn, not listed as objects`,
    );
  }

  return {
    format,
    group: gltf,
    meshes,
    elementMeshes: new Map<number, THREE.Mesh[]>(),
    elements: [],
    objects: objectLayer.objects,
    objectMeshes: objectLayer.objectMeshes,
    coordinationMatrix: null,
  };
}

/**
 * Build a USDZ model's scene content from its bytes — three's own USD loader
 * (card t_92f7411f: the fallback for a scan package that carries no GLB).
 *
 * A USDZ is a binary package (ZIP) whose root layer references its meshes as
 * `.usda`/`.usdc` siblings; `USDLoader.parse` unzips it and composes one
 * Group. Measured on the real RoomPlan capture (`room-1.usdz`, 258 KB, 70
 * entries): 69 meshes, ~113 ms, two `MeshPhysicalMaterial`s (white walls and
 * furniture, black door/window panels — RoomPlan ships no textures and no
 * layer colours, unlike the pipeline's GLB).
 *
 * The FRAME needs no fixing: the capture declares `upAxis = "Y"` in its own
 * root layer and the loader composes it as authored, so the USDZ and the GLB
 * of the same room land on exactly the same world bounds (measured: both
 * `8.001 × 2.675 × 10.217` m — the same scan through either file). As with a
 * glTF, this builder must not rotate or rescale anything.
 *
 * A USD carries no IFC semantics — no expressID, no storeys — so the element
 * map and the element list come back EMPTY, exactly as they do for a glTF:
 * the element panel hides, and the file's own node names are what the OBJECT
 * list reports (the RoomPlan nodes carry `Wall0`/`Chair1`/`Floor0`).
 */
export async function buildUsdModel(
  bytes: Uint8Array,
  applyMeshVisibility: (mesh: THREE.Mesh) => void,
): Promise<BuiltBimModel> {
  // Dynamic: the USD path — and the unzip it carries — is only reached when a
  // USDZ model is actually opened.
  const { USDLoader } = await import('three/examples/jsm/loaders/USDLoader.js');
  // The loader reads the package as one ArrayBuffer of its own: a view can be
  // a window onto a larger buffer, and TS's `ArrayBufferLike` may be shared.
  const usdBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(usdBuffer).set(bytes);

  let group: THREE.Group;
  try {
    group = new USDLoader().parse(usdBuffer);
  } catch (error) {
    // The loader's own refusal (a truncated package, a layer it cannot read)
    // is worth the caller's error message verbatim — the generic sentence
    // above it says nothing about which of the three formats failed.
    throw new Error(
      `The USDZ package could not be read: ${error instanceof Error ? error.message : String(error)}`,
    );
  }

  const meshes: THREE.Mesh[] = [];
  group.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if ((mesh as { isMesh?: boolean }).isMesh !== true) return;
    meshes.push(mesh);
    // A USD mesh may arrive without normals (RoomPlan's walls are planes):
    // without them every surface shades flat-black.
    if (!mesh.geometry.attributes.normal) mesh.geometry.computeVertexNormals();
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    const isTransparent = materials.some((material) => material.transparent === true);
    for (const material of materials) {
      // The same reason the glTF and IFC paths render DoubleSide: a wall seen
      // from inside a cut-open room must stay painted.
      material.side = THREE.DoubleSide;
    }
    mesh.castShadow = !isTransparent;
    mesh.receiveShadow = true;
  });

  if (meshes.length === 0) throw new Error('The USDZ package contained no meshes');

  // The object layer, exactly as the glTF builder builds it (card t_cb0aa19b):
  // a USDZ's nodes are RoomPlan's own (`Wall0`, `Chair1`), so a device anchor
  // can name any one of them.
  const objectLayer = collectObjectLayer(group);
  for (const mesh of meshes) applyMeshVisibility(mesh);

  return {
    format: 'usdz',
    group,
    meshes,
    elementMeshes: new Map<number, THREE.Mesh[]>(),
    elements: [],
    objects: objectLayer.objects,
    objectMeshes: objectLayer.objectMeshes,
    coordinationMatrix: null,
  };
}
